import React, { useEffect, useRef, useState } from "react";
import { ColumnsPanelTrigger, ExportPrint, FilterPanelTrigger, gridDensitySelector, GridToolbarContainer, GridViewColumnIcon, QuickFilter, QuickFilterTrigger } from "@mui/x-data-grid";
import { Box, Typography, Button, Stack, Tooltip, Badge, Divider, TextField, InputAdornment, useTheme, useMediaQuery, Toolbar, Menu, MenuItem, ListItemIcon, ListItemText, IconButton } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { ToolbarButton } from "@mui/x-data-grid";
import { GridFilterListIcon } from "@mui/x-data-grid";
import styled from "@emotion/styled";
import { Add, Cancel, Check, DeleteOutlined, Download, FileDownload, Help, Search, Settings } from "@mui/icons-material";
import { QuickFilterControl } from "@mui/x-data-grid";
import { QuickFilterClear } from "@mui/x-data-grid";
import { useGridApiContext } from "@mui/x-data-grid";
import { useGridSelector } from "@mui/x-data-grid";
import { ExportCsv } from "@mui/x-data-grid";


const StyledQuickFilter = styled(QuickFilter)({
  display: "grid",
  alignItems: "center",
});

const StyledTextField = styled(TextField)(({ theme, ownerState }) => ({
  gridArea: "1 / 1",
  overflowX: "clip",
  width: ownerState.expanded ? 260 : "var(--trigger-width)",
  opacity: ownerState.expanded ? 1 : 0,
  transition: theme.transitions.create(["width", "opacity"]),
}));

const StyledToolbarButton = styled(ToolbarButton)(({ theme, ownerState }) => ({
  gridArea: "1 / 1",
  width: "min-content",
  height: "min-content",
  zIndex: 1,
  opacity: ownerState.expanded ? 0 : 1,
  pointerEvents: ownerState.expanded ? "none" : "auto",
  transition: theme.transitions.create(["opacity"]),
}));



const CustomToolbar = ({ title, description, showDownload, showAdd, onAddClick, showDelete, onDelete }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const apiRef = useGridApiContext();
  const density = useGridSelector(apiRef, gridDensitySelector);
  const [densityMenuOpen, setDensityMenuOpen] = useState(false);
  const densityMenuTriggerRef = useRef(null);

  const densityOptions = [
    { label: 'Compact density', value: 'compact' },
    { label: 'Standard density', value: 'standard' },
    { label: 'Comfortable density', value: 'comfortable' },
  ];

  useEffect(() => {
    const savedDensity = localStorage.getItem("datagrid-density") || "compact";
    apiRef.current.setDensity(savedDensity);
  }, []);

  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuTriggerRef = useRef(null);

  return (
    <GridToolbarContainer>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%"
        }}
      >
        {/* Left side — title */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography
            variant="subtitle"
            fontWeight={700}
            sx={{
              // pl: 1,
              // fontWeight: 600,
              textTransform: "uppercase",
            }}
          >
            {title}
          </Typography>

          {description && <Tooltip
            title={description}
            arrow
            componentsProps={{
              tooltip: { sx: { whiteSpace: "pre-line" } },
            }}
          >
            <Help sx={{ ml: 1, fontSize: 21 }} />
          </Tooltip>}


        </Box>

        {/* Right side — optional buttons */}
        <Stack direction="row" spacing={1}>
          {/* Conditionally render Add button */}
          {showAdd && (
            <Button
              variant="contained"
              size="medium"
              fullWidth={isMobile}
              sx={{
                mr: 2,
                backgroundColor: "secondary.main",
                fontWeight: "600",
                "&:hover": { backgroundColor: "secondary.dark" },
                whiteSpace: "nowrap",
              }}
              onClick={onAddClick}
            >
              <Add />
            </Button>
          )}
          {/* Conditionally render Delete button */}
          {showDelete && (
            <Button
              variant="contained"
              size="medium"
              fullWidth={isMobile}
              sx={{
                backgroundColor: "secondary.main",
                fontWeight: "600",
                "&:hover": { backgroundColor: "secondary.dark" },
                whiteSpace: "nowrap",
              }}
              onClick={onDelete}
            >
              <DeleteOutlined />
            </Button>
          )}
          <Tooltip title="Columns">
            <ColumnsPanelTrigger render={<ToolbarButton />}>
              <GridViewColumnIcon fontSize="small" />
            </ColumnsPanelTrigger>
          </Tooltip>

          {/* Filters */}
          {/* <Tooltip title="Filters">
                  <FilterPanelTrigger
                    render={(props, state) => (
                      <ToolbarButton {...props} color="default">
                        <Badge
                          badgeContent={state.filterCount}
                          color="primary"
                          variant="dot"
                        >
                          <GridFilterListIcon fontSize="small" />
                        </Badge>
                      </ToolbarButton>
                    )}
                  />
                </Tooltip> */}

          {/* <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 0.5 }} /> */}

          {/* Quick Search */}
          <StyledQuickFilter>
            <QuickFilterTrigger
              render={(triggerProps, state) => (
                <Tooltip title="Search" enterDelay={0}>
                  <StyledToolbarButton
                    {...triggerProps}
                    ownerState={{ expanded: state.expanded }}
                    color="default"
                    aria-disabled={state.expanded}
                  >
                    <Search fontSize="small" />
                  </StyledToolbarButton>
                </Tooltip>
              )}
            />
            <QuickFilterControl
              render={({ ref, ...controlProps }, state) => (
                <StyledTextField
                  {...controlProps}
                  ownerState={{ expanded: state.expanded }}
                  inputRef={ref}
                  aria-label="Search"
                  placeholder="Search..."
                  size="small"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: state.value ? (
                        <InputAdornment position="end">
                          <QuickFilterClear
                            edge="end"
                            size="small"
                            aria-label="Clear search"
                            material={{ sx: { marginRight: -0.75 } }}
                          >
                            <Cancel fontSize="small" />
                          </QuickFilterClear>
                        </InputAdornment>
                      ) : null,
                      ...controlProps.slotProps?.input,
                    },
                    ...controlProps.slotProps,
                  }}
                />
              )}
            />
          </StyledQuickFilter>

          <Tooltip title="Density">
            <IconButton
              ref={densityMenuTriggerRef}
              size="small"
              onClick={() => setDensityMenuOpen(true)}
            >
              <Settings fontSize="small" />
            </IconButton>
          </Tooltip>

          <Menu
            id="density-menu"
            anchorEl={densityMenuTriggerRef.current}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            open={densityMenuOpen}
            onClose={() => setDensityMenuOpen(false)}
          >
            {densityOptions.map((option) => (
              <MenuItem
                key={option.value}
                onClick={() => {
                  apiRef.current.setDensity(option.value);
                  localStorage.setItem("datagrid-density", option.value);
                  setDensityMenuOpen(false);
                }}
              >
                <ListItemIcon>
                  {density === option.value && <Check fontSize="small" />}
                </ListItemIcon>
                <ListItemText>{option.label}</ListItemText>
              </MenuItem>
            ))}
          </Menu>

          {showDownload && <Tooltip title="Download">
            <ToolbarButton
              ref={exportMenuTriggerRef}
              id="export-menu-trigger"
              aria-controls="export-menu"
              aria-haspopup="true"
              aria-expanded={exportMenuOpen ? 'true' : undefined}
              onClick={() => setExportMenuOpen(true)}
            >
              <FileDownload fontSize="small" />
            </ToolbarButton>
          </Tooltip>
          }

          <Menu
            id="export-menu"
            anchorEl={exportMenuTriggerRef.current}
            open={exportMenuOpen}
            onClose={() => setExportMenuOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              list: {
                'aria-labelledby': 'export-menu-trigger',
              },
            }}
          >
            {/* <ExportPrint render={<MenuItem />} onClick={() => setExportMenuOpen(false)}>
          Print
        </ExportPrint> */}
            <ExportCsv render={<MenuItem />} onClick={() => setExportMenuOpen(false)}>
              Download as CSV
            </ExportCsv>
            {/* Available to MUI X Premium users */}
            {/* <ExportExcel render={<MenuItem />}>
           Download as Excel
          </ExportExcel> */}
          </Menu>


        </Stack>
      </Box>
    </GridToolbarContainer>
  );
};

export default CustomToolbar;
